import axios from "axios";
import configProvider from "config/configProvider";
import { GeocodeProximity } from "points/models";

/**
 * Can a van actually be driven to this coordinate?
 *
 * A location a crew cannot be driven to is not a location. Latitude and longitude are easy to
 * get wrong in ways that still look plausible on a form -- a transposed digit puts a yard in a
 * reservoir, and the coordinate is still "valid", still inside North America, still near the hub.
 * The map shows water and nobody reads the map.
 *
 * Asked as a driving route FROM THE HUB, which is the question that actually matters: not "is
 * this coordinate near a road somewhere" but "can the crew get there from where they start".
 *
 * TWO SIGNALS, and one is not enough. Measured against Mapbox Directions:
 *
 *   Love's Travel Stop, Amarillo        code Ok         snapped  14 m
 *   Rail yard interior, Alliance TX     code Ok         snapped  66 m
 *   Middle of Lake Meredith, TX         code Ok         snapped 1218 m
 *   Gulf of Mexico                      code NoSegment  no route
 *
 * Far out to sea there is no route at all. But the middle of a lake still ROUTES -- Directions
 * happily snaps the destination 1.2km sideways onto the nearest road and returns a perfectly good
 * route to a place the van cannot reach. So the snap distance is the signal that catches water,
 * and the missing route only catches the extreme case.
 */

const DIRECTIONS_URL = "https://api.mapbox.com/directions/v5/mapbox/driving";

/**
 * How far the destination may sit from the nearest drivable road.
 *
 * Deliberately generous. A rail yard interior measured 66m and a gate can be further still --
 * these are exactly the places this screen exists to add, and blocking one is worse than letting
 * through a coordinate that is merely awkward. A lake measured 1218m, so there is a wide margin
 * between the case worth catching and the case worth keeping.
 */
export const MAX_ROAD_SNAP_METERS = 500;

export interface DrivabilityResult {
  /** False only when we are confident a van cannot get there. Unknown counts as drivable. */
  isDrivable: boolean;
  /** Metres from the coordinate to the nearest drivable road, when Directions gave one. */
  snapMeters: number | null;
  /** Shown to the operator when it is not drivable. Empty otherwise. */
  reason: string;
}

/**
 * The route itself, which the drivability check throws away.
 *
 * geo.ClientPointToHub carries a Distance and a DriveTime per hub, and the add-a-point screen used
 * to send neither -- so the database wrote a 10000 placeholder into the column mileage bills on.
 * Directions has already been asked the exact question those columns answer, so the numbers come
 * off the same response rather than out of a second call.
 *
 * Metres and seconds, straight off the response and unconverted. The miles and minutes the columns
 * actually store are worked out in points/hubAssignment, which is where that conversion is tested.
 */
export interface RouteMeasurement {
  distanceMeters: number | null;
  durationSeconds: number | null;
}

/** Both readings of one Directions answer: can a van get there, and how far and long is the drive. */
export interface RouteReport {
  drivability: DrivabilityResult;
  measurement: RouteMeasurement;
}

const DRIVABLE: DrivabilityResult = { isDrivable: true, snapMeters: null, reason: "" };
const UNMEASURED: RouteMeasurement = { distanceMeters: null, durationSeconds: null };

/**
 * Turns a Directions response into a verdict.
 *
 * Kept pure and separate from the request so the thresholds can be tested without the network.
 */
export const classifyDirections = (data: any): DrivabilityResult => {
  const code = String(data?.code ?? "");

  // No route at all: open water, or somewhere with no road network in reach.
  if (code !== "Ok" || !Array.isArray(data?.routes) || data.routes.length === 0) {
    return {
      isDrivable: false,
      snapMeters: null,
      reason: "No driving route reaches that spot. Check the coordinates -- it may be in water.",
    };
  }

  // waypoints[1] is the destination as Directions SNAPPED it onto the road network. The distance
  // is how far it had to move the point to find a road, which is the real measure of "off-road".
  const destination = Array.isArray(data?.waypoints) ? data.waypoints[1] : null;
  const snap = Number(destination?.distance);
  if (!Number.isFinite(snap)) return DRIVABLE;

  if (snap > MAX_ROAD_SNAP_METERS) {
    return {
      isDrivable: false,
      snapMeters: Math.round(snap),
      reason:
        `The nearest road is about ${Math.round(snap)} m from that spot, so a van cannot be ` +
        "driven to it. Move the pin onto the gate or entrance the crew would actually use.",
    };
  }

  return { isDrivable: true, snapMeters: Math.round(snap), reason: "" };
};

/**
 * Reads the driven distance and duration off a Directions response.
 *
 * Kept pure and separate from the request, the same as classifyDirections, so the shape can be
 * tested without the network. Returns nulls rather than zeros for anything it cannot read: a zero
 * distance would claim the hub is at the location, which is a wrong number rather than a missing
 * one, and the server treats null as its cue to fall back to its own placeholder.
 */
export const readRouteMeasurement = (data: any): RouteMeasurement => {
  if (String(data?.code ?? "") !== "Ok" || !Array.isArray(data?.routes) || data.routes.length === 0) {
    return UNMEASURED;
  }

  const distanceMeters = Number(data.routes[0]?.distance);
  const durationSeconds = Number(data.routes[0]?.duration);

  return {
    distanceMeters: Number.isFinite(distanceMeters) ? distanceMeters : null,
    durationSeconds: Number.isFinite(durationSeconds) ? durationSeconds : null,
  };
};

/**
 * Asks Directions about the hub -> location drive and reads the answer both ways.
 *
 * Fails OPEN, on both readings and for the same reason. If the request errors, or there is no hub
 * yet, the verdict is "drivable" and the measurement is nulls -- this is a guard against a mistyped
 * coordinate, not an authority on where crews can go, and a Mapbox outage must not stop someone
 * adding a location or cost that location its hub. The server has its own checks, and it has its
 * own placeholder for a distance nobody could measure.
 */
const describeRoute = async (
  hub: GeocodeProximity | null | undefined,
  latitude: number,
  longitude: number
): Promise<RouteReport> => {
  if (!hub || !Number.isFinite(latitude) || !Number.isFinite(longitude)) {
    return { drivability: DRIVABLE, measurement: UNMEASURED };
  }

  try {
    const from = `${hub.longitude},${hub.latitude}`;
    const to = `${longitude},${latitude}`;
    const response = await axios.get(`${DIRECTIONS_URL}/${from};${to}`, {
      params: {
        access_token: configProvider.MapboxApiKey,
        // The waypoints, whether a route exists, and the route's own distance and duration. Still
        // no turn list -- overview:false does not suppress routes[0].distance or .duration.
        overview: "false",
        steps: false,
      },
    });

    return {
      drivability: classifyDirections(response.data),
      measurement: readRouteMeasurement(response.data),
    };
  } catch {
    return { drivability: DRIVABLE, measurement: UNMEASURED };
  }
};

/**
 * Checks whether a coordinate can be driven to from the hub.
 *
 * Fails OPEN, as described on describeRoute above.
 */
const checkDrivable = async (
  hub: GeocodeProximity | null | undefined,
  latitude: number,
  longitude: number
): Promise<DrivabilityResult> => (await describeRoute(hub, latitude, longitude)).drivability;

const routingService = {
  checkDrivable,
  describeRoute,
};

export default routingService;
